<ng-template vdrDialogTitle>{{ "Bulk Add Free Gifts" | translate }}</ng-template>

<form [formGroup]="detailForm">
  <!-- Product Selection (only show when NOT in bulk apply mode) -->
  <vdr-form-field [label]="'Available Products' | translate" *ngIf="!isBulkApplyMode">
    
    <!-- Loading State -->
    <ng-container *ngIf="loading && !allProductsLoaded">
      <div class="text-muted">
        {{ "Loading products information..." | translate }}
      </div>
    </ng-container>

    <!-- Available Products List -->
    <ng-container *ngIf="allProductsLoaded && availableProducts.length > 0">
      <div class="products-list">
        <div 
          *ngFor="let product of availableProducts" 
          class="product-item"
          [class.selected]="selectedProductId === product.id"
          [class.disabled]="!product.hasConditions"
          (click)="product.hasConditions ? selectProduct(product) : null"
        >
          <div class="product-header">
            <div class="product-info">
              <strong>{{ product.name }}</strong>
              <span class="product-id text-muted ml-2">(ID: {{ product.id }})</span>
            </div>
            <div class="product-status">
              <span 
                *ngIf="product.loading" 
                class="badge badge-info"
              >
                Loading...
              </span>
              <span 
                *ngIf="!product.loading && product.hasConditions" 
                class="badge badge-success"
              >
                {{ product.freeGiftSelection?.conditions?.length || 0 }} Free Gift Condition(s)
              </span>
              <span 
                *ngIf="!product.loading && !product.hasConditions" 
                class="badge badge-warning"
              >
                No Free Gift Conditions
              </span>
            </div>
          </div>
          
          <!-- Show conditions summary if selected -->
          <div 
            *ngIf="selectedProductId === product.id && product.freeGiftSelection?.conditions" 
            class="conditions-summary mt-2"
          >
            <div class="text-muted mb-1">Available Conditions:</div>
            <div 
              *ngFor="let condition of product.freeGiftSelection.conditions" 
              class="condition-item"
            >
              <small>{{ condition.name || 'Unnamed Condition' }}</small>
            </div>
          </div>
        </div>
      </div>
    </ng-container>

    <!-- No Products Found -->
    <ng-container *ngIf="allProductsLoaded && availableProducts.length === 0">
      <div class="text-warning">
        <clr-icon shape="warning-standard"></clr-icon>
        {{ "No products found in selected package reviews." | translate }}
      </div>
    </ng-container>

    <!-- Error State - fallback to manual selection -->
    <ng-container *ngIf="allProductsLoaded && availableProducts.length === 0">
      <div class="mt-3">
        <vdr-form-field [label]="'Manual Product Selection' | translate">
          <select 
            formControlName="productId" 
            class="form-control"
            (change)="onManualProductSelection($event)"
          >
            <option value="">{{ "Select a product..." | translate }}</option>
            <option 
              *ngFor="let productId of getAvailableProductIds()" 
              [value]="productId"
            >
              Product ID: {{ productId }}
            </option>
          </select>
        </vdr-form-field>
      </div>
    </ng-container>

  </vdr-form-field>

  <!-- Products Overview in Bulk Apply Mode -->
  <div class="mt-3" *ngIf="isBulkApplyMode && allProductsLoaded">
    <h4>{{ "Products Overview" | translate }}</h4>
    <div class="products-overview">
      <div class="row">
        <!-- Products with conditions -->
        <div class="col-md-6" *ngIf="getProductsWithConditions().length > 0">
          <h5 class="text-success">{{ "Products with Free Gift Conditions" | translate }} ({{ getProductsWithConditions().length }})</h5>
          <div class="products-list small-list">
            <div 
              *ngFor="let product of getProductsWithConditions()" 
              class="product-item success"
              [class.expanded]="isProductExpanded(product.id)"
              (click)="toggleProductExpansion(product.id)"
            >
              <div class="product-header">
                <div class="product-info">
                  <strong>{{ product.name }}</strong>
                  <small class="text-muted d-block">
                    {{ product.freeGiftSelection?.conditions?.length || 0 }} condition(s) • 
                    {{ getPackagesContainingProduct(product.id).length }} package(s)
                  </small>
                </div>
                <div class="product-actions">
                  <span class="badge badge-success">Eligible</span>
                  <clr-icon 
                    shape="angle" 
                    direction="right" 
                    class="expand-icon ml-2"
                    [class.expanded]="isProductExpanded(product.id)"
                  ></clr-icon>
                </div>
              </div>
              
              <!-- Expanded condition details -->
              <div 
                *ngIf="isProductExpanded(product.id) && product.freeGiftSelection?.conditions" 
                class="product-conditions-detail"
              >
                <div class="text-muted mb-2">
                  <strong>Free Gift Conditions:</strong>
                </div>
                <div 
                  *ngFor="let condition of product.freeGiftSelection.conditions" 
                  class="condition-detail-item"
                >
                  <div class="condition-info">
                    <span>{{ "Buy" | translate }} <strong>{{ condition.minQuantity }}</strong> {{ "get" | translate }} 
                    <strong>{{ condition.giftQuantity }}</strong> {{ "free" | translate }}</span>
                  </div>
                  <div class="gift-variants" *ngIf="condition.productVariants && condition.productVariants.length > 0">
                    <small class="text-muted">{{ "Available gifts:" | translate }}</small>
                    <ul class="gift-list">
                      <li *ngFor="let variant of condition.productVariants">
                        {{ variant.name }} ({{ variant.sku }})
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Products without conditions -->
        <div class="col-md-6" *ngIf="getProductsWithoutConditions().length > 0">
          <h5 class="text-muted">{{ "Products without Free Gift Conditions" | translate }} ({{ getProductsWithoutConditions().length }})</h5>
          <div class="products-list small-list">
            <div 
              *ngFor="let product of getProductsWithoutConditions()" 
              class="product-item disabled"
            >
              <div class="product-header">
                <div class="product-info">
                  <strong>{{ product.name }}</strong>
                  <small class="text-muted d-block">
                    No conditions • {{ getPackagesContainingProduct(product.id).length }} package(s)
                  </small>
                </div>
                <span class="badge badge-warning">Not Eligible</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Free Gift Information (only show when NOT in bulk apply mode and product is selected) -->
  <div class="mt-3" *ngIf="!isBulkApplyMode && freeGiftSelection && freeGiftSelection.conditions">
    <h4>{{ "Free Gift Conditions" | translate }}</h4>
    <div class="free-gift-conditions">
      <div *ngFor="let condition of freeGiftSelection.conditions" class="condition-item">
        <div class="condition-info">
          <span>{{ "Buy" | translate }} <strong>{{ condition.minQuantity }}</strong> {{ "get" | translate }} 
          <strong>{{ condition.giftQuantity }}</strong> {{ "free" | translate }}</span>
        </div>
        <div class="gift-variants" *ngIf="condition.productVariants">
          <small class="text-muted">{{ "Available gifts:" | translate }}</small>
          <ul class="gift-list">
            <li *ngFor="let variant of condition.productVariants">
              {{ variant.name }} ({{ variant.sku }})
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>

  <!-- No Free Gifts Warning (only show when NOT in bulk apply mode) -->
  <div class="mt-3 alert alert-warning" *ngIf="!isBulkApplyMode && freeGiftSelection && (!freeGiftSelection.conditions || freeGiftSelection.conditions.length === 0)">
    <clr-icon shape="warning-standard"></clr-icon>
    {{ "No free gift conditions found for this product" | translate }}
  </div>

  <!-- Invalid Orders -->
  <div class="mt-3" *ngIf="invalidOrders && invalidOrders.length > 0">
    <h4 class="text-danger">{{ "Invalid Orders" | translate }}</h4>
    <div class="invalid-orders">
      <div *ngFor="let order of invalidOrders" class="invalid-order-item">
        <clr-icon shape="error-standard" class="is-error"></clr-icon>
        <span>{{ "Order" | translate }} {{ order.code }}</span>
      </div>
    </div>
  </div>

  <!-- Bulk Apply Mode Notice -->
  <div class="mt-3 alert alert-info" *ngIf="isBulkApplyMode">
    <clr-icon shape="info-circle"></clr-icon>
    <strong>Bulk Apply Mode:</strong> You have selected {{ items.length }} package review(s) with {{ getProductsWithConditions().length }} eligible products. 
    <br><br>
    
    <div class="mt-2">
      <strong>Products that will receive free gifts:</strong>
      <ul class="mt-1 mb-0">
        <li *ngFor="let product of getProductsWithConditions()">
          {{ product.name }} 
          <span class="text-muted">
            ({{ product.freeGiftSelection?.conditions?.length || 0 }} condition(s), 
            in {{ getPackagesContainingProduct(product.id).length }} package(s))
          </span>
        </li>
      </ul>
    </div>
  </div>

  <!-- Summary -->
  <div class="mt-3 summary-section">
    <h4>{{ "Summary" | translate }}</h4>
    <div class="summary-info">
      <div>{{ "Selected Package Reviews:" | translate }} <strong>{{ items.length }}</strong></div>
      <div *ngIf="isBulkApplyMode">
        {{ "Products with Free Gift Conditions:" | translate }} <strong>{{ getProductsWithConditions().length }}</strong>
      </div>
      <div *ngIf="selectedProductId && !isBulkApplyMode">
        {{ "Selected Product:" | translate }} <strong>{{ productName || selectedProductId }}</strong>
        <br>
        <small class="text-muted">
          {{ getPackagesContainingProduct(selectedProductId).length }} out of {{ items.length }} packages contain this product
        </small>
      </div>
      <div *ngIf="checkResult && checkResult.valid" class="text-success">
        <clr-icon shape="check-circle" class="is-success"></clr-icon>
        {{ checkResult.message || "All packages are valid for free gift application" }}
      </div>
      <div *ngIf="checkResult && !checkResult.valid" class="text-danger">
        <clr-icon shape="error-standard" class="is-error"></clr-icon>
        {{ checkResult.message }}
      </div>
    </div>
  </div>
</form>

<ng-template vdrDialogButtons>
  <button
    type="button"
    class="btn"
    (click)="cancel()"
    [disabled]="loading"
  >
    {{ "common.cancel" | translate }}
  </button>
  <button
    type="button"
    class="btn btn-primary"
    (click)="checkBulkOperation()"
    [disabled]="loading || checking || (!selectedProductId && !isBulkApplyMode)"
  >
    <clr-icon shape="check" *ngIf="!checking"></clr-icon>
    <span *ngIf="checking">Checking...</span>
    <span *ngIf="isBulkApplyMode">Check All Products</span>
    <span *ngIf="!isBulkApplyMode">{{ "Check Eligibility" | translate }}</span>
  </button>
  <button
    type="submit"
    class="btn btn-primary"
    (click)="save()"
    [disabled]="loading || (!isBulkApplyMode && (!selectedProductId || !freeGiftSelection || (freeGiftSelection && (!freeGiftSelection.conditions || freeGiftSelection.conditions.length === 0))))"
  >
    <clr-icon shape="plus" *ngIf="!loading"></clr-icon>
    <span *ngIf="loading">Adding...</span>
    <span *ngIf="isBulkApplyMode">Add Free Gifts for All Products</span>
    <span *ngIf="!isBulkApplyMode">{{ "Add Free Gifts" | translate }}</span>
  </button>
</ng-template> 